import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight } from 'lucide-react';
import ClientsMarquee from '../components/ClientsMarquee';
import ExportArrowsMap from '../components/ExportArrowsMap';
import { PRODUCT_TABS, STATS } from '../mock/mock';

/**
 * Landing product tile — uniform aspect-square frame so all four verticals
 * share a clean symmetric footprint, with videos filling edge-to-edge.
 */
const ProductTile = ({ p }) => {
  return (
    <Link
      to={`/products?tab=${p.id}`}
      className="group relative block overflow-hidden bg-[#1a1614] aspect-square"
    >
      <video
        src={p.video}
        autoPlay loop muted playsInline preload="auto"
        className="absolute inset-0 w-full h-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-105 transition-all duration-[1200ms]"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-[#1a1614]/85 via-[#1a1614]/25 to-transparent" />

      {/* Category tag */}
      <div className="absolute top-6 right-6 md:top-8 md:right-8 bg-[#faf6f0]/95 text-[#1a1614] text-[10px] tracking-[0.28em] uppercase px-3 py-1.5">
        {p.tag}
      </div>

      {/* Content */}
      <div className="absolute left-6 right-6 bottom-6 md:left-10 md:right-10 md:bottom-10 text-[#faf6f0]">
        <p className="text-xs tracking-[0.28em] uppercase text-[#d4b483] mb-3">{p.tagline}</p>
        <h3 className="font-serif text-3xl md:text-4xl lg:text-5xl leading-tight mb-4">
          {p.name}
        </h3>
        <div className="inline-flex items-center gap-2 text-sm tracking-wide text-[#faf6f0] group-hover:gap-4 transition-all">
          Discover <ArrowUpRight size={16} className="text-[#d4b483]" />
        </div>
      </div>
    </Link>
  );
};

const Landing = () => {
  return (
    <div>
      {/* HERO — no bg video; features animated export map */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#faf6f0] via-[#f2ebde] to-[#e8dfd1] pt-10 md:pt-16 pb-16 md:pb-20">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-start">
          {/* Left copy — aligned to top so the caption sits on the same line as the map header */}
          <div className="lg:col-span-4 lg:pr-2 order-2 lg:order-1">
            <p className="rcd-fade-up text-[10px] md:text-xs tracking-[0.32em] uppercase text-[#a67c3d] mb-3">
              Rajdhani Crafts &amp; Daughters &middot; Jaipur &middot; Family Legacy Since 1964
            </p>
            <h1 className="rcd-fade-up rcd-delay-1 font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] text-[#1a1614] mb-6 mt-6">
              A legacy furniture<br />
              <span className="italic text-[#a67c3d]">manufacturing house.</span>
            </h1>
            <p className="rcd-fade-up rcd-delay-2 text-base md:text-lg text-[#4a3f36] max-w-xl leading-relaxed mb-8">
              Rooted in Jaipur, <strong>Rajdhani Crafts and Daughters</strong> brings generations of craftsmanship and modern in-house manufacturing together to create bespoke furniture for distinguished interiors <span className="text-[#1a1614] font-medium">worldwide</span>.
            </p>
            <div className="rcd-fade-up rcd-delay-3 flex flex-wrap gap-3">
              <Link
                to="/products"
                className="group inline-flex items-center gap-2 px-6 py-3 bg-[#1a1614] text-[#faf6f0] text-sm tracking-wide hover:bg-[#a67c3d] transition-colors duration-300"
              >
                Explore Products
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/about"
                className="group inline-flex items-center gap-2 px-6 py-3 border border-[#1a1614] text-[#1a1614] text-sm tracking-wide hover:bg-[#1a1614] hover:text-[#faf6f0] transition-colors duration-300"
              >
                Discover Our Legacy
              </Link>
            </div>
          </div>

          {/* Right — animated export map */}
          <div className="lg:col-span-8 order-1 lg:order-2">
            <div className="rcd-fade-up rcd-delay-1">
              <p className="text-[10px] md:text-xs tracking-[0.32em] uppercase text-[#a67c3d] mb-3 text-center lg:text-left">
                Global Footprints &middot; Where We Ship Our Craft
              </p>
              <ExportArrowsMap />
            </div>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="bg-[#1a1614] text-[#faf6f0] py-14">
        <div className="w-full px-6 lg:px-10 grid grid-cols-1 md:grid-cols-3 gap-8 justify-items-center">
          {STATS.map(s => (
            <div key={s.label} className="text-center">
              <div className="font-serif text-4xl md:text-5xl text-[#d4b483] mb-2">{s.value}</div>
              <div className="text-xs md:text-sm tracking-[0.2em] uppercase text-[#c9bfae]">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* OUR PRODUCTS — big boxes */}
      <section className="pt-16 md:pt-24 pb-24 md:pb-32 bg-[#f2ebde]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-14 gap-4">
            <div>
              <p className="text-xs tracking-[0.32em] uppercase text-[#a67c3d] mb-3">Our Products</p>
              <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-[#1a1614] leading-[1.05]">
                Four verticals.<br />
                <span className="italic text-[#a67c3d]">One</span> Jaipur manufacturing house.
              </h2>
            </div>
            <Link to="/products" className="rcd-link text-sm text-[#1a1614] font-medium">
              Explore all products &rarr;
            </Link>
          </div>

          {/* Product tiles — each frame adapts to its own video's natural rotation
              (no cropping and no letterbox). */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 items-start">
            {PRODUCT_TABS.map((p) => (
              <ProductTile key={p.id} p={p} />
            ))}
          </div>
        </div>
      </section>

      {/* HOSPITALITY LEADERS — running marquee after products */}
      <section className="relative">
        <ClientsMarquee background="#faf6f0" speed={65} showName={false} />
      </section>

      {/* CTA */}
      <section className="py-24 bg-[#1a1614] text-[#faf6f0]">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 text-center">
          <h2 className="font-serif text-4xl md:text-5xl mb-6">Have a project in mind?</h2>
          <p className="text-[#c9bfae] max-w-2xl mx-auto mb-10 text-lg">
            Share your drawings, moodboards, specifications and timelines &mdash; the team will study the requirement and respond around the needs of the project.
          </p>
          <Link to="/contact" className="inline-flex items-center gap-2 px-8 py-4 bg-[#d4b483] text-[#1a1614] hover:bg-[#faf6f0] transition-colors font-medium">
            Start a Conversation <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Landing;
